// src/views/auth/ResetPassword.js
// --- UPDATE (Forgot / reset password) -----------------------------------------
// /auth/reset-password?token=… — the page the reset email links to (also opened from the phone).
//   • Checks the link first (GET /auth/reset-password/verify): used / expired / replaced / invalid
//     links get a clear message and a "Request a new link" button.
//   • The token is removed from the address bar as soon as it is read (it is kept in memory only),
//     so it does not end up in screenshots, history or shared URLs.
//   • New password + confirm, show / hide, Caps Lock warning, live strength + the server's rules.
//   • POST /auth/reset-password → success screen → sign-in page (?reset=success) after 5 s.
// --- END UPDATE ---------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Box, Button, FormControl, FormLabel, FormErrorMessage, Heading, Icon, IconButton, Input, InputGroup,
    InputLeftElement, InputRightElement, Text, VStack, HStack, Alert, AlertIcon, AlertDescription, Spinner,
    useColorModeValue,
} from '@chakra-ui/react';
import { MdLockOutline, MdOutlineRemoveRedEye, MdArrowBack, MdKeyboardCapslock, MdCheckCircle, MdLinkOff, MdLockReset } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { AuthPageLayout, PasswordStrength, authApi, checkPassword, MIN_LENGTH_DEFAULT } from './passwordResetShared';

const REDIRECT_S = 5;

const readToken = (search) => {
    try { return new URLSearchParams(search).get('token') || ''; } catch { return ''; }
};

export default function ResetPassword() {
    const textColor = useColorModeValue('gray.800', 'white');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const brand = useColorModeValue('blue.600', 'blue.300');
    const inputBg = useColorModeValue('gray.50', 'gray.800');
    const inputFocusBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.600');
    const iconMuted = useColorModeValue('gray.400', 'gray.500');
    const doneBg = useColorModeValue('green.50', 'rgba(72,187,120,0.12)');

    const location = useLocation();
    const navigate = useNavigate();
    const tokenRef = useRef(readToken(location.search));
    const [check, setCheck] = useState({ state: tokenRef.current ? 'checking' : 'invalid', message: tokenRef.current ? null : 'This page needs the link from your reset email.' });
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [capsLock, setCapsLock] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [problems, setProblems] = useState([]);
    const [done, setDone] = useState(null);
    const [countdown, setCountdown] = useState(REDIRECT_S);

    // keep the token out of the address bar / history
    useEffect(() => {
        if (!tokenRef.current) return;
        try { window.history.replaceState(window.history.state, '', `${location.pathname}`); } catch { /* old browsers */ }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!tokenRef.current) return undefined;
        const ctrl = new AbortController();
        authApi(`/reset-password/verify?token=${encodeURIComponent(tokenRef.current)}`, { signal: ctrl.signal })
            .then((r) => setCheck({ state: 'valid', email: r.email_masked, expiresAt: r.expires_at, minLength: r.min_length || MIN_LENGTH_DEFAULT }))
            .catch((err) => {
                if (err.name === 'AbortError') return;
                if (err.status === 400) setCheck({ state: 'invalid', message: err.data?.message || err.message, reason: err.data?.reason });
                else setCheck({ state: 'error', message: 'FlowSense could not check your link. Check your connection and try again.' });
            });
        return () => ctrl.abort();
    }, []);

    useEffect(() => {
        if (!done) return undefined;
        if (countdown <= 0) { navigate('/auth/sign-in?reset=success', { replace: true }); return undefined; }
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [done, countdown, navigate]);

    const minLength = check.minLength || MIN_LENGTH_DEFAULT;
    const strength = useMemo(() => checkPassword(password, { minLength, confirm }), [password, confirm, minLength]);
    const minutesLeft = check.expiresAt ? Math.max(0, Math.round((new Date(check.expiresAt).getTime() - Date.now()) / 60000)) : null;

    const onKey = useCallback((e) => { if (typeof e.getModifierState === 'function') setCapsLock(e.getModifierState('CapsLock')); }, []);

    const submit = useCallback(async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError(null); setProblems([]);
        if (!strength.ok) {
            setProblems(strength.rules.filter((r) => !r.ok).map((r) => r.label));
            return;
        }
        setSubmitting(true);
        try {
            const r = await authApi('/reset-password', { method: 'POST', body: { token: tokenRef.current, password } });
            tokenRef.current = '';
            setPassword(''); setConfirm('');
            setDone({ message: r.message, username: r.username });
        } catch (err) {
            if (err.status === 422) setProblems(err.problems?.length ? err.problems : [err.message]);
            else if (err.status === 400) setCheck({ state: 'invalid', message: err.message, reason: err.code });
            else if (err.status === 429) setError(err.message);
            else if (err.status === 403) setError(err.message);
            else setError('Your password could not be changed. Check your connection and try again.');
        } finally {
            setSubmitting(false);
        }
    }, [submitting, strength, password]);

    let content;
    if (done) {
        content = (
            <Box data-testid="reset-done">
                <HStack spacing={3} mb={4}><Icon as={MdCheckCircle} boxSize={8} color="green.400" /><Heading color={textColor} fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800">Password changed</Heading></HStack>
                <Box p={4} borderRadius="xl" bg={doneBg} mb={5}>
                    <Text fontSize="sm" color={textColor}>{done.message}</Text>
                    {done.username && <Text fontSize="sm" color={muted} mt={1}>Username: <b>{done.username}</b></Text>}
                </Box>
                <Text fontSize="sm" color={muted} mb={4}>Use the new password on the web dashboard and in the FlowSense Tech app. Taking you to sign in in {countdown}s…</Text>
                <Button w="100%" h="52px" borderRadius="xl" color="white" bgGradient="linear(to-r, blue.500, purple.600)" _hover={{ bgGradient: 'linear(to-r, blue.600, purple.700)' }}
                    onClick={() => navigate('/auth/sign-in?reset=success', { replace: true })}>
                    Sign in now
                </Button>
            </Box>
        );
    } else if (check.state === 'checking') {
        content = (
            <HStack spacing={3} py={10} justify="center" data-testid="reset-checking"><Spinner /><Text color={muted}>Checking your reset link…</Text></HStack>
        );
    } else if (check.state === 'invalid' || check.state === 'error') {
        content = (
            <Box data-testid="reset-invalid">
                <HStack spacing={3} mb={4}><Icon as={MdLinkOff} boxSize={8} color="orange.400" /><Heading color={textColor} fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800">{check.state === 'error' ? 'Could not check the link' : 'This link can’t be used'}</Heading></HStack>
                <Alert status={check.state === 'error' ? 'error' : 'warning'} borderRadius="lg" mb={5}><AlertIcon /><AlertDescription fontSize="sm">{check.message}</AlertDescription></Alert>
                {check.state === 'error'
                    ? <Button w="100%" borderRadius="xl" onClick={() => window.location.reload()}>Try again</Button>
                    : (
                        <Button as={NavLink} to="/auth/forgot-password" w="100%" h="52px" borderRadius="xl" color="white" bgGradient="linear(to-r, blue.500, purple.600)"
                            _hover={{ bgGradient: 'linear(to-r, blue.600, purple.700)' }} leftIcon={<Icon as={MdLockReset} />}>
                            Request a new link
                        </Button>
                    )}
            </Box>
        );
    } else {
        content = (
            <>
                <VStack spacing={1} align="flex-start" mb={6}>
                    <Heading color={textColor} fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800">Choose a new password</Heading>
                    <Text fontSize="md" color={muted}>
                        For the account {check.email ? <b>{check.email}</b> : 'linked to this email'}.{minutesLeft !== null ? ` This link expires in about ${minutesLeft} minute${minutesLeft === 1 ? '' : 's'}.` : ''}
                    </Text>
                </VStack>
                {error && <Alert status="error" borderRadius="lg" mb={4} role="alert"><AlertIcon /><AlertDescription fontSize="sm">{error}</AlertDescription></Alert>}
                {problems.length > 0 && (
                    <Alert status="warning" borderRadius="lg" mb={4} role="alert" alignItems="flex-start" data-testid="reset-problems">
                        <AlertIcon />
                        <VStack align="stretch" spacing={0.5}>{problems.map((p) => <Text key={p} fontSize="sm">{p}</Text>)}</VStack>
                    </Alert>
                )}
                <Box as="form" onSubmit={submit} noValidate aria-label="Reset password form">
                    <VStack spacing={5} align="stretch">
                        {/* hidden username helps password managers save the new password against the right account */}
                        <input type="text" name="username" autoComplete="username" value={done?.username || ''} readOnly hidden />
                        <FormControl isRequired>
                            <FormLabel fontSize="sm" fontWeight="semibold" color={textColor} htmlFor="new-password">New password</FormLabel>
                            <InputGroup size="lg">
                                <InputLeftElement pointerEvents="none"><Icon as={MdLockOutline} color={iconMuted} /></InputLeftElement>
                                <Input id="new-password" type={show ? 'text' : 'password'} borderRadius="xl" value={password} autoFocus
                                    onChange={(e) => { setPassword(e.target.value); setProblems([]); }} onKeyDown={onKey} onKeyUp={onKey} onBlur={() => setCapsLock(false)}
                                    bg={inputBg} _focus={{ bg: inputFocusBg }} borderColor={border} autoComplete="new-password" isDisabled={submitting} placeholder="New password" />
                                <InputRightElement>
                                    <IconButton size="sm" variant="ghost" type="button" color="gray.400" icon={<Icon as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye} />}
                                        onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'} />
                                </InputRightElement>
                            </InputGroup>
                            {capsLock && <HStack spacing={1} mt={2} color="orange.400" role="status"><Icon as={MdKeyboardCapslock} /><Text fontSize="xs" fontWeight="semibold">Caps Lock is on</Text></HStack>}
                        </FormControl>
                        <FormControl isRequired isInvalid={confirm.length > 0 && confirm !== password}>
                            <FormLabel fontSize="sm" fontWeight="semibold" color={textColor} htmlFor="confirm-password">Confirm new password</FormLabel>
                            <InputGroup size="lg">
                                <InputLeftElement pointerEvents="none"><Icon as={MdLockOutline} color={iconMuted} /></InputLeftElement>
                                <Input id="confirm-password" type={show ? 'text' : 'password'} borderRadius="xl" value={confirm}
                                    onChange={(e) => { setConfirm(e.target.value); setProblems([]); }} onKeyDown={onKey} onKeyUp={onKey}
                                    bg={inputBg} _focus={{ bg: inputFocusBg }} borderColor={border} autoComplete="new-password" isDisabled={submitting} placeholder="Type it again" />
                            </InputGroup>
                            <FormErrorMessage>The passwords do not match yet.</FormErrorMessage>
                        </FormControl>
                        <PasswordStrength password={password} confirm={confirm} minLength={minLength} />
                        <Button type="submit" w="100%" h="52px" borderRadius="xl" fontWeight="bold" color="white"
                            bgGradient="linear(to-r, blue.500, purple.600)" _hover={{ bgGradient: 'linear(to-r, blue.600, purple.700)', boxShadow: 'lg' }}
                            isLoading={submitting} loadingText="Saving…" isDisabled={!password || !confirm}>
                            Change password
                        </Button>
                    </VStack>
                </Box>
            </>
        );
    }

    return (
        <AuthPageLayout panelTitle="Choose a password only you know." panelText="Use it for the FlowSense web dashboard and the FlowSense Tech app. Never share it — FlowSense staff will never ask for it.">
            {content}
            {!done && (
                <HStack mt={6} justify="center">
                    <Text as={NavLink} to="/auth/sign-in" fontSize="sm" color={brand} fontWeight="semibold" display="inline-flex" alignItems="center" gap={1} _hover={{ textDecoration: 'underline' }}>
                        <Icon as={MdArrowBack} /> Back to sign in
                    </Text>
                </HStack>
            )}
        </AuthPageLayout>
    );
}
